/**
 * HH:mm 타임 문자열 유틸 — TimePicker / 운영시간 min·max 판별용.
 */

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const parseTimeValue = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim().slice(0, 5);
  return TIME_RE.test(trimmed) ? trimmed : null;
};

export const formatTimeDisplay = (value?: string | null): string =>
  parseTimeValue(value) ?? '';

export const timeToMinutes = (value?: string | null): number | null => {
  const parsed = parseTimeValue(value);
  if (!parsed) return null;
  const [h, m] = parsed.split(':').map(Number);
  return h * 60 + m;
};

export const minutesToTime = (total: number): string => {
  const normalized = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const isTimeWithinRange = (
  value: string,
  min?: string | null,
  max?: string | null,
): boolean => {
  const minutes = timeToMinutes(value);
  if (minutes == null) return false;
  const minMinutes = timeToMinutes(min);
  const maxMinutes = timeToMinutes(max);
  if (minMinutes != null && minutes < minMinutes) return false;
  if (maxMinutes != null && minutes > maxMinutes) return false;
  return true;
};

export const buildHourOptions = (
  min?: string | null,
  max?: string | null,
): number[] => {
  const minMinutes = timeToMinutes(min) ?? 0;
  const maxMinutes = timeToMinutes(max) ?? 23 * 60 + 59;
  const hours: number[] = [];
  for (let h = 0; h < 24; h += 1) {
    const hourStart = h * 60;
    const hourEnd = hourStart + 59;
    if (hourEnd < minMinutes || hourStart > maxMinutes) continue;
    hours.push(h);
  }
  return hours;
};

export const buildMinuteOptions = (
  hour: number,
  min?: string | null,
  max?: string | null,
  step = 1,
): number[] => {
  const minMinutes = timeToMinutes(min) ?? 0;
  const maxMinutes = timeToMinutes(max) ?? 23 * 60 + 59;
  const minutes: number[] = [];
  for (let m = 0; m < 60; m += step) {
    const total = hour * 60 + m;
    if (total < minMinutes || total > maxMinutes) continue;
    minutes.push(m);
  }
  return minutes;
};

export const composeTimeValue = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
